"use client";

import React from "react";
import { Key, Users, ShieldAlert, FileCheck, Layers3 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const faqs = [{
  icon: Key,
  question: "How are AI usage costs handled?",
  answer: "EloRAG uses a BYO-Key model. You provide your own LLM and OCR keys, so variable usage is billed directly by your chosen providers instead of being bundled into the platform subscription."
}, {
  icon: Users,
  question: "What if we need more capacity or tenant scale?",
  answer: "All plans use hard backend-enforced caps. When you need more room, you can upgrade your plan or add Capacity Packs depending on which part of the deployment is becoming constrained."
}, {
  icon: ShieldAlert,
  question: "Are there unlimited usage plans?",
  answer: "No. EloRAG does not use unlimited usage positioning. Hard caps are part of the commercial model so scaling behavior stays predictable and overages do not become vague or uncontrolled."
}, {
  icon: FileCheck,
  question: "Can the AI still guess if evidence is missing?",
  answer: "No. EloRAG is designed to answer from approved content only. If sufficient evidence is not available in approved sources, the system refuses instead of generating a confident-sounding guess."
}];
export default function SubscriptionHelp() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="subscription-help" className="bg-background py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={ref} className={cn("mx-auto max-w-2xl text-center transition-all duration-700 ease-out", isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
            <Layers3 className="size-4" />
            Before you book
          </div>

          <h2 className="text-foreground mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">
            Common questions
          </h2>

          <p className="text-muted-foreground mt-6 text-lg leading-8">
            A few important points about pricing, scaling, and controlled RAG behavior before you schedule a call.
          </p>
        </div>

        <div className="mx-auto mt-14 max-w-2xl lg:mt-16 lg:max-w-none">
          <div className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-8 lg:max-w-none lg:grid-cols-2">
            {faqs.map((faq, i) => {
            const Icon = faq.icon;
            return <Card key={i} data-index={i} className={cn("border-border bg-card shadow-sm transition-all duration-700 ease-out", isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")} style={{
              transitionDelay: `${i * 100}ms`
            }}>
                  <CardHeader className="flex flex-row items-center gap-4 pb-2">
                    <div className="bg-primary/10 flex size-12 shrink-0 items-center justify-center rounded-lg">
                      <Icon className="text-primary size-6" aria-hidden="true" />
                    </div>
                    <CardTitle className="text-foreground text-xl">
                      {faq.question}
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <p className="text-muted-foreground text-base leading-7">
                      {faq.answer}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
      </div>
    </section>;
}